/**
 * IT Services Section
 * Integrates IT Career Services into the Jobs pages
 * Shows contextual service suggestions based on user actions
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  Briefcase,
  ChevronRight,
  Sparkles,
  Target,
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import ITCareerServicesSection, {
  CVReviewCard,
  InterviewPrepCard,
  CodingMentorshipCard,
  CandidateScreeningCard,
  ITCareerMobilePill,
  ITCareerMobileSheet,
  ITServiceTriggerPrompt,
} from './ITCareerServicesCards';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency config
const CURRENCY_CONFIG = {
  UK: { code: 'GBP', symbol: '£' },
  SL: { code: 'LKR', symbol: 'Rs' },
  US: { code: 'USD', symbol: '$' },
};

/**
 * Main IT Services Section for Jobs Page
 * Shows at top of Technology & IT Jobs category
 */
export const ITServicesTopSection = ({ category }) => {
  const { country } = useCountry();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileSheet, setShowMobileSheet] = useState(false);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;

  // Only show for IT jobs category
  const isITCategory = category === 'technology-it';

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/it-services/providers`, {
          params: { country, category: 'candidate' }
        });
        if (response.data.providers) {
          setProviders(response.data.providers);
        }
      } catch (error) {
        console.error('Error fetching IT providers:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isITCategory) {
      fetchProviders();
    }
  }, [country, isITCategory]);

  if (!isITCategory || loading) return null;

  return (
    <>
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200" data-testid="it-services-top-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-600" />
              IT Career Services
            </CardTitle>
            <Badge variant="outline" className="bg-white">
              <Target className="h-3 w-3 mr-1" />
              Recommended for you
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <ITCareerServicesSection
            providers={providers}
            currency={currency}
            country={country}
          />
        </CardContent>
      </Card>

      {/* Mobile Pill */}
      <Sheet open={showMobileSheet} onOpenChange={setShowMobileSheet}>
        <ITCareerMobilePill onClick={() => setShowMobileSheet(true)} />
        <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>IT Career Services</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <ITCareerMobileSheet currency={currency} country={country} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};


/**
 * Inline IT Services (shown between job listings)
 */
export const ITServicesInlineCard = ({ position = 5 }) => {
  const { country } = useCountry();
  const [provider, setProvider] = useState(null);
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/it-services/providers`, {
          params: { country, service_type: 'cv_review', limit: 1 }
        });
        if (response.data.providers?.[0]) {
          setProvider(response.data.providers[0]);
        }
      } catch (error) {
        console.error('Error fetching provider:', error);
      }
    };
    fetchProvider();
  }, [country]);

  return (
    <div className="my-4" data-testid={`it-services-inline-${position}`}>
      <CVReviewCard provider={provider} currency={currency} country={country} />
    </div>
  );
};


/**
 * Job Detail Page - "Improve Your Chances" Section
 * Shows after user views or applies to a job
 */
export const ImproveYourChancesSection = ({ 
  job,
  triggerType = 'view_it_job', // view_it_job, save_apply_job, etc.
}) => {
  const { country } = useCountry();
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;
  
  // Check if it's an IT job
  const isITJob = job?.category === 'technology-it';

  useEffect(() => {
    const fetchTriggerServices = async () => {
      if (!isITJob) return;
      
      try {
        // Get trigger config
        const triggerResponse = await axios.get(
          `${API_URL}/api/it-services/triggers/${triggerType}`,
          { params: { country } }
        );
        
        if (triggerResponse.data.trigger) {
          setMessage(triggerResponse.data.trigger.message);
          setServices(triggerResponse.data.services || []);
        }
        
        // Get providers for these services
        const providerResponse = await axios.get(`${API_URL}/api/it-services/providers`, {
          params: { country, category: 'candidate' }
        });
        if (providerResponse.data.providers) {
          setProviders(providerResponse.data.providers);
        }
      } catch (error) {
        console.error('Error fetching trigger services:', error);
      }
    };
    
    fetchTriggerServices();
  }, [country, triggerType, isITJob]);

  if (!isITJob || dismissed || services.length === 0) return null;

  // Get primary service to show
  const primaryService = services[0];
  const cvProvider = providers.find(p => p.service_types?.includes('cv_review'));
  const interviewProvider = providers.find(p => p.service_types?.includes('interview_prep'));

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mt-6" data-testid="improve-chances-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          {message || 'Want to improve your chances?'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {primaryService?.id === 'cv_review' && (
            <CVReviewCard provider={cvProvider} currency={currency} country={country} />
          )}
          {primaryService?.id === 'interview_prep' && (
            <InterviewPrepCard provider={interviewProvider} currency={currency} country={country} />
          )}
          {services.length > 1 && services[1]?.id === 'interview_prep' && (
            <InterviewPrepCard provider={interviewProvider} currency={currency} country={country} />
          )}
        </div>
        
        <div className="flex justify-end mt-3">
          <Button variant="ghost" size="sm" onClick={() => setDismissed(true)}>
            Not now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


/**
 * Post-Application Prompt
 * Shown after user submits a job application
 */
export const PostApplicationPrompt = ({ jobTitle, companyName }) => {
  const { country } = useCountry();
  const [provider, setProvider] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/it-services/providers`, {
          params: { country, service_type: 'interview_prep', limit: 1 }
        });
        if (response.data.providers?.[0]) {
          setProvider(response.data.providers[0]);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchProvider();
  }, [country]);

  if (dismissed) return null;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4" data-testid="post-application-prompt">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-green-100 rounded-full">
          <Sparkles className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">Application Submitted!</h4>
          <p className="text-sm text-gray-600 mt-1">
            You applied for <strong>{jobTitle}</strong> at <strong>{companyName}</strong>
          </p>
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Want to improve your chances?
            </p>
            <Card className="bg-white">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💬</span>
                    <div>
                      <p className="font-medium text-sm">Technical Interview Prep</p>
                      <p className="text-xs text-gray-500">Online</p>
                    </div>
                  </div>
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                    Book session
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-600"
        >
          ×
        </button>
      </div>
    </div>
  );
};


/**
 * Employer Services Section
 * For employers posting IT jobs
 */
export const EmployerServicesSection = ({ employerEmail }) => {
  const { country } = useCountry();
  const [providers, setProviders] = useState([]);
  
  const currency = CURRENCY_CONFIG[country] || CURRENCY_CONFIG.UK;

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/it-services/providers`, {
          params: { country, category: 'employer' }
        });
        if (response.data.providers) {
          setProviders(response.data.providers);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchProviders();
  }, [country]);

  const screeningProvider = providers.find(p => p.service_types?.includes('candidate_screening'));

  return (
    <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="employer-services-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-amber-600" />
          Hiring Tools
        </CardTitle>
      </CardHeader>
      <CardContent>
        <CandidateScreeningCard 
          provider={screeningProvider} 
          currency={currency} 
          country={country} 
        />
      </CardContent>
    </Card>
  );
};


export default ITServicesTopSection;
